'use client';

import { forwardRef, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { globalOverlayProps } from '../../lib/global-overlay';
import { Z_INDEX } from '../../lib/z-index';
import { SCircleProgress } from '../SCircleProgress';

export interface SLoadingContainerProps {
  /** 오버레이 표시 여부 (Stencil의 show()/hide() 명령형 API → 제어 prop으로 대체) */
  open?: boolean;
  /** 스피너 하단 메시지 */
  message?: string;
  /** z-index. 기본값(1200)은 토스트(1100)·모달 호스트(1000) 위 — 전체 화면을 막는 최상단 블로킹 오버레이 */
  zIndex?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * SLoadingContainer — sd-loading-container 포팅.
 * 전체 화면 딤 오버레이 + inverse 스피너. React에서는 `open` 제어 prop 사용.
 * body 로 portal 해 조상의 스태킹 컨텍스트에 갇히지 않고 항상 최상단에 뜨게 한다.
 */
export const SLoadingContainer = forwardRef<HTMLDivElement, SLoadingContainerProps>(
  function SLoadingContainer(
    { open = false, message, zIndex = Z_INDEX.loading, className, style },
    ref,
  ) {
    // portal 은 클라이언트에서만 (SSR 시 document 없음) — 마운트 후 body 로 렌더
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // 오버레이 표시 중 배경 스크롤 차단
    useEffect(() => {
      if (!open) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }, [open]);

    // body 로 portal 된 블로킹 오버레이라, 클릭이 document 까지 전파되면 Radix Dialog 의
    // "바깥 클릭" 감지에 걸려 열려있는 모달이 닫힌다. 오버레이 요소에 네이티브 pointerdown
    // 리스너를 직접 붙여(요소 자신에서 가장 먼저 발화) 전파를 끊어 document 리스너를 차단한다.
    const innerRef = useRef<HTMLDivElement | null>(null);
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );
    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const stop = (e: Event) => e.stopPropagation();
      el.addEventListener('pointerdown', stop);
      el.addEventListener('mousedown', stop);
      return () => {
        el.removeEventListener('pointerdown', stop);
        el.removeEventListener('mousedown', stop);
      };
    }, [mounted]);

    const overlay = (
      <div
        ref={setRef}
        {...globalOverlayProps}
        aria-hidden={!open}
        className={cn(
          'fixed inset-0 flex items-center justify-center transition-opacity duration-200',
          open
            ? 'pointer-events-auto visible opacity-100'
            : 'pointer-events-none invisible opacity-0',
          className,
        )}
        style={{ zIndex, ...style }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative flex flex-col items-center gap-[16px]">
          <SCircleProgress indeterminate type="inverse" />
          {message && (
            <p className="m-0 max-w-[400px] break-keep text-center text-[14px] font-medium leading-[22px] text-[color:var(--color-white)]">
              {message}
            </p>
          )}
        </div>
      </div>
    );

    if (!mounted) return null;
    return createPortal(overlay, document.body);
  },
);
